import { useAction, useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import type { Id } from "@convex/_generated/dataModel";
import {
  Breadcrumb,
  Button,
  Checkbox,
  Input,
  Label,
  PageContainer,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import { arianeOrganisation } from "../../lib/ariane";

export function UserModifier() {
  const { id: orgId, userId } = useParams<{ id: string; userId: string }>();
  const navigate = useNavigate();

  const user = useQuery(
    api.superAdmin.obtenirUser,
    userId ? { userId: userId as Id<"users"> } : "skip",
  );
  const org = useQuery(
    api.superAdmin.obtenirOrganisation,
    orgId ? { organisationId: orgId as Id<"organisations"> } : "skip",
  );
  const groupes = useQuery(
    api.superAdmin.listerGroupes,
    orgId ? { organisationId: orgId as Id<"organisations"> } : "skip",
  );
  const modifier = useMutation(api.superAdmin.modifierUser);
  const reinitialiser = useAction(api.superAdmin.reinitialiserMotDePasseUser);

  const [nom, setNom] = useState("");
  const [isGestionnaire, setIsGestionnaire] = useState(false);
  const [groupId, setGroupId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [mdp, setMdp] = useState("");
  const [mdpConfirm, setMdpConfirm] = useState("");
  const [mdpSaving, setMdpSaving] = useState(false);
  const [mdpMessage, setMdpMessage] = useState<{ ok: boolean; texte: string } | null>(null);

  useEffect(() => {
    if (user) {
      setNom(user.name ?? "");
      setIsGestionnaire(!user.groupId);
      setGroupId(user.groupId ?? "");
    }
  }, [user]);

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!userId) return;
    if (!isGestionnaire && !groupId) {
      setError("Sélectionnez un groupe, ou cochez « Gestionnaire ».");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await modifier({
        userId: userId as Id<"users">,
        nom,
        role: isGestionnaire
          ? { kind: "gestionnaire" }
          : { kind: "groupe", groupId: groupId as Id<"groups"> },
      });
      void navigate(`/organisations/${orgId}/users`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Erreur inconnue");
    } finally {
      setSaving(false);
    }
  };

  const handleMotDePasse = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!userId) return;
    if (mdp !== mdpConfirm) {
      setMdpMessage({ ok: false, texte: "Les mots de passe ne correspondent pas." });
      return;
    }
    setMdpMessage(null);
    setMdpSaving(true);
    try {
      await reinitialiser({ userId: userId as Id<"users">, nouveauMotDePasse: mdp });
      setMdp("");
      setMdpConfirm("");
      setMdpMessage({
        ok: true,
        texte: "Mot de passe réinitialisé. L'utilisateur a été déconnecté de ses sessions.",
      });
    } catch (err: unknown) {
      setMdpMessage({ ok: false, texte: err instanceof Error ? err.message : "Erreur inconnue" });
    } finally {
      setMdpSaving(false);
    }
  };

  if (user === undefined) {
    return (
      <div className="flex justify-center p-16">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  if (user === null) {
    return <div className="p-8 text-center text-muted-foreground">Utilisateur introuvable</div>;
  }

  return (
    <PageContainer>
      <Breadcrumb
        items={[...arianeOrganisation(orgId, org?.nom, "users"), { label: user.name || user.email || "Utilisateur" }]}
      />
      <h1 className="text-2xl font-bold mb-1 text-center">Modifier l'utilisateur</h1>
      <p className="text-sm text-muted-foreground mb-6 text-center">{user.email}</p>

      <form
        onSubmit={handleSubmit}
        className="bg-card border border-border rounded-surface p-6 space-y-4 max-w-xl mx-auto"
      >
        <div>
          <Label htmlFor="user-nom" className="mb-1">Nom</Label>
          <Input id="user-nom" value={nom} onChange={(e) => setNom(e.target.value)} />
        </div>

        {user.isAdmin ? (
          <p className="text-sm text-muted-foreground">
            Super-admin : le rôle dans l'organisation ne s'applique pas.
          </p>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Checkbox
                id="isGestionnaire"
                checked={isGestionnaire}
                onCheckedChange={(coche) => setIsGestionnaire(coche === true)}
              />
              <label htmlFor="isGestionnaire" className="text-sm font-medium">
                Gestionnaire
              </label>
              <span className="text-xs text-muted-foreground">
                (accès à tous les parcours de l'organisation)
              </span>
            </div>

            {!isGestionnaire && (
              <div>
                <label id="user-groupe-label" className="block text-sm font-medium mb-1">
                  Groupe
                </label>
                <Select
                  value={groupId || undefined}
                  onValueChange={setGroupId}
                  disabled={!groupes || groupes.length === 0}
                >
                  <SelectTrigger className="w-full" aria-labelledby="user-groupe-label">
                    <SelectValue placeholder="Sélectionner un groupe…" />
                  </SelectTrigger>
                  <SelectContent>
                    {groupes?.map((g: any) => (
                      <SelectItem key={g._id} value={g._id}>
                        {g.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Changer de groupe y déplace aussi les campagnes et parcours
                  créés par cet utilisateur.
                </p>
              </div>
            )}
          </>
        )}

        {error && (
          <p className="text-sm text-destructive bg-destructive/10 rounded-controle px-3 py-2">
            {error}
          </p>
        )}

        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={saving} className="flex-1">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
          <Button asChild variant="outline">
            <Link to={`/organisations/${orgId}/users`}>Annuler</Link>
          </Button>
        </div>
      </form>

      <form
        onSubmit={handleMotDePasse}
        className="bg-card border border-border rounded-surface p-6 space-y-4 max-w-xl mx-auto mt-6"
      >
        <div>
          <h2 className="font-semibold">Réinitialiser le mot de passe</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Fixe un nouveau mot de passe à transmettre à l'utilisateur. Ses
            sessions ouvertes sont fermées.
          </p>
        </div>
        <div>
          <Label htmlFor="user-mdp" className="mb-1">Nouveau mot de passe</Label>
          <Input
            id="user-mdp"
            type="password"
            required
            minLength={8}
            value={mdp}
            onChange={(e) => setMdp(e.target.value)}
            placeholder="8 caractères minimum"
          />
        </div>
        <div>
          <Label htmlFor="user-mdp-confirm" className="mb-1">Confirmer</Label>
          <Input
            id="user-mdp-confirm"
            type="password"
            required
            minLength={8}
            value={mdpConfirm}
            onChange={(e) => setMdpConfirm(e.target.value)}
          />
        </div>
        {mdpMessage && (
          <p
            className={`text-sm rounded-controle px-3 py-2 ${mdpMessage.ok ? "text-green-700 bg-green-500/10" : "text-destructive bg-destructive/10"}`}
          >
            {mdpMessage.texte}
          </p>
        )}
        <Button type="submit" variant="outline" disabled={mdpSaving}>
          {mdpSaving ? "Réinitialisation…" : "Réinitialiser"}
        </Button>
      </form>
    </PageContainer>
  );
}
